import { Link } from 'react-router-dom'
import {
  ArrowRight,
  ChartNoAxesCombined,
  Check,
  CreditCard,
  Gauge,
  Globe2,
  LayoutTemplate,
  MousePointerClick,
  Palette,
  Repeat2,
  ShieldCheck,
  Sparkles,
  SquareStack,
  Target,
  Zap,
} from 'lucide-react'

import { SeoHead } from '@/components/seo/seo-head'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardDescription, CardTitle } from '@/components/ui/card'
import { SectionHeading } from '@/components/ui/section-heading'
import { createMetadata, marketingSeoPages } from '@/lib/seo'
import { cn } from '@/lib/utils'

const coreCapabilities = [
  {
    icon: CreditCard,
    title: 'Donation experiences',
    description: 'Forms and widgets for taking donations on your site.',
    bullets: [
      'Inline donation forms',
      'Popup and donate buttons',
      'Suggested amounts',
      'Mobile checkout',
    ],
    visual: 'donation',
  },
  {
    icon: Target,
    title: 'Campaign pages',
    description: 'Build and publish pages for a specific fundraising goal.',
    bullets: [
      'Campaign landing pages',
      'Emergency appeals',
      'Recurring giving',
      'Donor-friendly checkout',
    ],
    visual: 'campaign',
  },
  {
    icon: Repeat2,
    title: 'Recurring giving',
    description: 'Offer one-time and monthly giving in the same flow.',
    bullets: [
      'One-time or monthly',
      'Suggested and custom amounts',
      'Recurring gift records',
      'Stripe-managed subscriptions',
    ],
    visual: 'recurring',
  },
  {
    icon: ChartNoAxesCombined,
    title: 'Donations and receipts',
    description: 'Records, receipts, and reporting in one place.',
    bullets: [
      'Donor records',
      'Automatic receipts',
      'Donation history',
      'Simple reporting',
    ],
    visual: 'analytics',
  },
] as const

const walkthroughSteps = [
  {
    step: 'Step 1',
    title: 'Add a donation button',
    description: 'Embed it on your website.',
    bullets: ['WordPress', 'Webflow', 'Custom websites'],
    visual: 'embed',
  },
  {
    step: 'Step 2',
    title: 'Donors give',
    description: 'A mobile-first flow for amount, donor details, and secure payment.',
    bullets: ['Suggested amounts', 'One-time or monthly', 'Stripe payment fields'],
    visual: 'checkout',
  },
  {
    step: 'Step 3',
    title: 'Track results',
    description: 'Review donation activity and source reporting in one dashboard.',
    bullets: ['Donation dashboard', 'Source labels', 'Revenue trends'],
    visual: 'tracking',
  },
] as const

const websiteSupport = [
  {
    icon: Globe2,
    title: 'WordPress',
    description: 'Install the free KindLumen plugin for a guided account connection, or add the embed snippet.',
  },
  {
    icon: LayoutTemplate,
    title: 'Squarespace, Wix & Webflow',
    description: 'Add the form where your builder and current plan permit script-based custom HTML.',
  },
  {
    icon: SquareStack,
    title: 'Custom HTML',
    description: 'Use the generated two-line snippet on pages that allow an async script and custom HTML.',
  },
] as const

const nonprofitFeatures = [
  {
    icon: Zap,
    title: 'Three-part setup',
    description: 'Connect Stripe, configure a form, and add the generated embed to your website.',
  },
  {
    icon: Sparkles,
    title: 'Designed around giving',
    description: 'A modern, mobile-first checkout with suggested amounts and recurring options.',
  },
  {
    icon: Palette,
    title: 'Branded to match',
    description: 'Your logo, colors, and copy on a form that fits your site.',
  },
  {
    icon: Repeat2,
    title: 'Recurring built in',
    description: 'Offer one-time and monthly giving in the same form.',
  },
] as const

const securitySignals = [
  'Powered by Stripe — PCI-DSS Level 1 payment infrastructure',
  'Money never touches KindLumen — donations go straight to your account',
  'SSL-encrypted checkout and secure donor data handling',
] as const

const metadata = createMetadata(marketingSeoPages.features)

function DonationVisual() {
  return (
    <div className="w-full rounded-[34px] border border-line bg-white p-5 shadow-[0_28px_80px_rgba(28,44,82,0.14)] sm:p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-ink-muted">Donation form</p>
          <p className="mt-2 text-xl font-semibold text-ink">Emergency relief</p>
        </div>
        <div className="rounded-full bg-brand/10 px-3 py-1 text-xs font-semibold text-brand">Live preview</div>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-3">
        {['$25', '$75', '$150', '$300'].map((amount, index) => (
          <div
            key={amount}
            className={cn(
              'rounded-[20px] px-4 py-4 text-center text-base font-semibold',
              index === 1 ? 'bg-brand text-white shadow-[0_16px_30px_rgba(37,99,235,0.22)]' : 'border border-line bg-surface-strong text-ink',
            )}
          >
            {amount}
          </div>
        ))}
      </div>
      <div className="mt-4 rounded-[22px] border border-line bg-surface-strong p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-ink">Make this monthly</p>
            <p className="mt-1 text-sm text-ink-soft">Help families every month</p>
          </div>
          <div className="flex h-7 w-12 items-center rounded-full bg-brand px-1">
            <div className="ml-auto h-5 w-5 rounded-full bg-white" />
          </div>
        </div>
      </div>
      <div className="mt-4 rounded-[22px] bg-night px-5 py-4 text-center text-sm font-semibold text-white">
        Continue to secure checkout
      </div>
    </div>
  )
}

function CampaignVisual() {
  return (
    <div className="relative w-full rounded-[36px] border border-line bg-white p-5 shadow-[0_28px_80px_rgba(28,44,82,0.14)] sm:p-6">
      <div className="rounded-[28px] bg-[linear-gradient(135deg,#DBE7FF,#BFD4FF)] p-5">
        <div className="rounded-[22px] bg-[linear-gradient(135deg,#2563EB,#0EA5E9)] p-5 text-white">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/75">Campaign page</p>
            <span className="rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/85">
              Sample data
            </span>
          </div>
          <p className="mt-3 max-w-xs text-2xl font-semibold leading-[1.16]">Help fund a new community center.</p>
          <div className="mt-6 h-2 rounded-full bg-white/20">
            <div className="h-full w-[62%] rounded-full bg-white" />
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-white/80">
            <span>$62,000 raised</span>
            <span>Goal: $100,000</span>
          </div>
        </div>
      </div>
      <div className="mt-5 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center">
        <div>
          <div className="h-3 w-40 rounded-full bg-mist" />
          <div className="mt-3 h-3 w-full rounded-full bg-mist" />
          <div className="mt-2 h-3 w-4/5 rounded-full bg-mist" />
        </div>
        <div className="rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white">Donate now</div>
      </div>
    </div>
  )
}

function RecurringVisual() {
  return (
    <div className="w-full rounded-[34px] border border-line bg-white p-5 shadow-[0_28px_80px_rgba(28,44,82,0.14)] sm:p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-ink-muted">Recurring giving</p>
          <p className="mt-2 text-xl font-semibold text-ink">Monthly donations</p>
        </div>
        <div className="rounded-full bg-brand/10 px-3 py-1 text-xs font-semibold text-brand">Sample data</div>
      </div>
      <div className="mt-6 rounded-[24px] border border-line bg-surface-strong p-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-ink">Recurring gift activity</p>
          <div className="rounded-full border border-line bg-white px-3 py-1 text-xs font-semibold text-ink">$25/mo</div>
        </div>
        <div className="mt-4 flex items-end gap-3">
          {[30, 44, 56, 68, 82].map((height) => (
            <div key={height} className="flex flex-1 items-end">
              <div
                className="w-full rounded-t-[18px] bg-[linear-gradient(180deg,#2563EB,#0EA5E9)]"
                style={{ height }}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="rounded-[22px] border border-line bg-surface-strong p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-ink-muted">Monthly gifts</p>
          <p className="mt-2 text-2xl font-semibold text-ink">86</p>
        </div>
        <div className="rounded-[22px] border border-line bg-surface-strong p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-ink-muted">Recurring volume</p>
          <p className="mt-2 text-2xl font-semibold text-ink">$18.4k</p>
        </div>
      </div>
    </div>
  )
}

function AnalyticsVisual() {
  return (
    <div className="w-full rounded-[34px] border border-line bg-white p-5 shadow-[0_28px_80px_rgba(28,44,82,0.14)] sm:p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-ink-muted">Analytics</p>
          <p className="mt-2 text-xl font-semibold text-ink">Campaign performance</p>
        </div>
        <div className="rounded-full bg-brand/10 px-3 py-1 text-xs font-semibold text-brand">Sample data</div>
      </div>
      <div className="mt-6 flex h-44 items-end gap-3 rounded-[26px] border border-line bg-surface-strong p-4">
        {[42, 58, 46, 72, 88, 94].map((height) => (
          <div key={height} className="flex flex-1 items-end">
            <div
              className="w-full rounded-t-[18px] bg-[linear-gradient(180deg,#2563EB,#0EA5E9)]"
              style={{ height: `${height}%` }}
            />
          </div>
        ))}
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        {[
          ['Total raised', '$8.4k'],
          ['Recurring mix', '38%'],
          ['Top source', 'Homepage'],
        ].map(([label, value]) => (
          <div key={label} className="rounded-[22px] border border-line bg-surface-strong p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-ink-muted">{label}</p>
            <p className="mt-2 text-xl font-semibold text-ink">{value}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

function WalkthroughVisual({ type }: { type: (typeof walkthroughSteps)[number]['visual'] }) {
  if (type === 'embed') {
    return (
      <div className="w-full rounded-[30px] border border-line bg-white p-5 shadow-soft">
        <div className="rounded-[22px] border border-line bg-surface-strong p-5">
          <p className="text-sm font-semibold text-ink">Embed on your site</p>
          <div className="mt-4 grid gap-3">
            {['Homepage hero', 'Campaign page', 'Donate button'].map((item) => (
              <div key={item} className="flex items-center justify-between rounded-[18px] bg-white px-4 py-3 text-sm font-medium text-ink shadow-soft">
                <span>{item}</span>
                <MousePointerClick className="h-4 w-4 text-brand" />
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (type === 'checkout') {
    return (
      <div className="w-full rounded-[30px] border border-line bg-white p-5 shadow-soft">
        <div className="rounded-[22px] bg-night p-5 text-white">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/55">Secure checkout</p>
          <div className="mt-4 grid grid-cols-2 gap-3">
            {['$35', '$75', '$150', '$300'].map((amount, index) => (
              <div key={amount} className={cn('rounded-[18px] px-4 py-3 text-center text-sm font-semibold', index === 1 ? 'bg-white text-night' : 'border border-white/10 bg-white/10')}>
                {amount}
              </div>
            ))}
          </div>
          <div className="mt-4 rounded-[18px] bg-white/10 px-4 py-3 text-sm text-white/75">Make this monthly</div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full rounded-[30px] border border-line bg-white p-5 shadow-soft">
      <div className="rounded-[22px] border border-line bg-surface-strong p-5">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-ink">Donation dashboard</p>
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-brand/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-brand">
              Sample data
            </span>
            <Gauge className="h-4 w-4 text-brand" />
          </div>
        </div>
        <div className="mt-5 flex h-28 items-end gap-3">
          {[28, 36, 48, 40, 60].map((height) => (
            <div key={height} className="flex flex-1 items-end">
              <div className="w-full rounded-t-[18px] bg-[linear-gradient(180deg,#2563EB,#0EA5E9)]" style={{ height }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function CapabilityVisual({ type }: { type: (typeof coreCapabilities)[number]['visual'] }) {
  if (type === 'donation') {
    return <DonationVisual />
  }

  if (type === 'campaign') {
    return <CampaignVisual />
  }

  if (type === 'recurring') {
    return <RecurringVisual />
  }

  return <AnalyticsVisual />
}

function CapabilitySection({
  capability,
  reverse = false,
}: {
  capability: (typeof coreCapabilities)[number]
  reverse?: boolean
}) {
  const Icon = capability.icon

  return (
    <div className="panel p-6 sm:p-8 xl:p-10">
      <div
        className={cn(
          'grid gap-10 xl:grid-cols-[minmax(320px,0.42fr)_minmax(420px,0.58fr)] xl:items-center',
          reverse && 'xl:grid-cols-[minmax(420px,0.58fr)_minmax(320px,0.42fr)]',
        )}
      >
        <div className={cn('space-y-7', reverse && 'xl:order-2')}>
          <div className="flex h-12 w-12 items-center justify-center rounded-[18px] bg-brand/10 text-brand">
            <Icon className="h-5 w-5" />
          </div>
          <div className="space-y-3">
            <h2 className="max-w-[34rem] text-4xl font-semibold leading-[1.14] sm:text-[2.9rem]">{capability.title}</h2>
            <p className="max-w-[34rem] text-base leading-7 text-ink-soft sm:text-lg sm:leading-8">{capability.description}</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {capability.bullets.map((bullet) => (
              <div key={bullet} className="flex items-start gap-3 rounded-[22px] border border-line bg-white/80 px-4 py-4">
                <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand/10">
                  <Check className="h-3.5 w-3.5 text-brand" />
                </div>
                <p className="text-sm leading-6 text-ink-soft">{bullet}</p>
              </div>
            ))}
          </div>
        </div>

        <div className={cn('xl:justify-self-stretch', reverse && 'xl:order-1')}>
          <div className="mx-auto w-full max-w-[760px]">{<CapabilityVisual type={capability.visual} />}</div>
        </div>
      </div>
    </div>
  )
}

export default function FeaturesPage() {
  return (
    <div className="space-y-16 pb-20 pt-10 sm:space-y-20">
      <SeoHead metadata={metadata} />
      <section className="marketing-shell">
        <div className="grid gap-8 xl:grid-cols-[0.44fr_0.56fr] xl:items-center">
          <div className="space-y-7 py-4 lg:pr-8">
            <div className="space-y-4">
              <div className="eyebrow">Features</div>
              <h1 className="max-w-2xl text-balance text-[2.45rem] font-semibold leading-[1.08] sm:text-[3.35rem] sm:leading-[1.04]">
                A free Stripe donation form for the website you already have.
              </h1>
              <p className="max-w-2xl text-base leading-7 text-ink-soft sm:text-lg sm:leading-8">
                Branded one-time and monthly donation forms you connect to your own Stripe account and embed on a site that supports custom HTML and scripts — no platform fees.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg">
                <Link to="/signup">Create your donation form</Link>
              </Button>
              <Button asChild size="lg" variant="secondary">
                <Link to="/pricing">View pricing</Link>
              </Button>
            </div>

            <p className="text-sm font-medium text-ink-soft">Connects to your own Stripe account.</p>
          </div>

          <div className="mx-auto w-full max-w-[940px]">
            <div className="relative overflow-hidden rounded-[30px] border border-white/10 bg-night p-5 shadow-glow sm:p-7">
              <div className="space-y-6">
                <div className="space-y-4">
                  <Badge className="border-white/15 bg-white/10 text-white">Product tour</Badge>
                  <h2 className="max-w-xl text-3xl font-semibold leading-[1.12] text-white sm:text-[2.4rem]">
                    Create it, embed it, start collecting.
                  </h2>
                </div>

                <div className="mx-auto w-full max-w-[680px]">
                  <DonationVisual />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-shell">
        <div className="space-y-8">
          <SectionHeading eyebrow="Core capabilities" title="Four things most teams need." />

          <div className="space-y-8 lg:space-y-10">
            {coreCapabilities.map((capability, index) => (
              <CapabilitySection key={capability.title} capability={capability} reverse={index % 2 === 1} />
            ))}
          </div>
        </div>
      </section>

      <section className="marketing-shell">
        <div className="panel p-6 sm:p-8 xl:p-10">
          <SectionHeading eyebrow="How it works" title="From embed to donation." />

          <div className="mt-10 grid gap-6 xl:grid-cols-3">
            {walkthroughSteps.map((step) => (
              <Card key={step.step} className="h-full overflow-hidden">
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-brand/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-brand">
                    {step.step}
                  </div>
                </div>
                <CardTitle className="mt-5 text-2xl">{step.title}</CardTitle>
                <CardDescription className="mt-4">{step.description}</CardDescription>
                <div className="mt-6">{<WalkthroughVisual type={step.visual} />}</div>
                <div className="mt-6 flex flex-wrap gap-2">
                  {step.bullets.map((bullet) => (
                    <span key={bullet} className="rounded-full border border-line bg-surface-strong px-3 py-1 text-xs font-semibold text-ink-soft">
                      {bullet}
                    </span>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="marketing-shell">
        <div className="panel p-6 sm:p-8 xl:p-10">
          <div className="grid gap-8 xl:grid-cols-[0.38fr_0.62fr] xl:items-center">
            <div className="space-y-5">
              <SectionHeading eyebrow="Website compatibility" title="Works with the site you have." />
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              {websiteSupport.map((item) => {
                const Icon = item.icon
                return (
                  <Card key={item.title} className="h-full">
                    <div className="flex h-11 w-11 items-center justify-center rounded-[18px] bg-brand/10 text-brand">
                      <Icon className="h-5 w-5" />
                    </div>
                    <CardTitle className="mt-5 text-xl">{item.title}</CardTitle>
                    <CardDescription className="mt-4">{item.description}</CardDescription>
                  </Card>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-shell">
        <div className="panel p-6 sm:p-8 xl:p-10">
          <div className="grid gap-8 xl:grid-cols-[0.42fr_0.58fr] xl:items-center">
            <div className="space-y-5">
              <SectionHeading
                eyebrow="Stripe payments"
                title="Use your own Stripe account."
                description="Donations are processed by Stripe and paid out through the Stripe account you connect."
              />
              <div className="grid gap-3">
                {[
                  'Full control of payouts',
                  'Transparent reporting',
                  'No platform-held funds',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3 rounded-[22px] border border-line bg-white/80 px-4 py-4">
                    <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand/10">
                      <Check className="h-3.5 w-3.5 text-brand" />
                    </div>
                    <p className="text-sm leading-6 text-ink-soft">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              {([
                ['Cards', 'Supported card networks'],
                ['Apple Pay', 'When available for the donor'],
                ['Google Pay', 'When available for the donor'],
              ] as const).map(([paymentMethod, availability]) => (
                <div key={paymentMethod} className="rounded-[24px] border border-line bg-white px-5 py-8 text-center shadow-soft">
                  <p className="text-lg font-semibold text-ink">{paymentMethod}</p>
                  <p className="mt-2 text-xs leading-5 text-ink-muted">{availability}</p>
                </div>
              ))}
              <p className="text-xs leading-5 text-ink-muted sm:col-span-3">
                Wallet availability depends on Stripe eligibility, the donor’s device and browser, and the connected account’s configuration.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-shell">
        <div className="panel p-6 sm:p-8 xl:p-10">
          <div className="grid gap-8 xl:grid-cols-[0.42fr_0.58fr] xl:items-center">
            <div className="space-y-5">
              <SectionHeading eyebrow="Why teams choose it" title="Built around the site and Stripe account you already use." />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {nonprofitFeatures.map((item) => (
                <Card key={item.title} className="h-full bg-white">
                  <div className="flex h-11 w-11 items-center justify-center rounded-[18px] bg-brand/10 text-brand">
                    <item.icon className="h-5 w-5" />
                  </div>
                  <CardTitle className="mt-5 text-xl">{item.title}</CardTitle>
                  <CardDescription className="mt-4">{item.description}</CardDescription>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-shell">
        <div className="panel p-6 sm:p-8 xl:p-10">
          <div className="grid gap-8 xl:grid-cols-[0.4fr_0.6fr] xl:items-center">
            <div className="space-y-5">
              <SectionHeading eyebrow="Security and trust" title="Donor-safe infrastructure." />
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              {securitySignals.map((item) => (
                <div key={item} className="rounded-[24px] border border-line bg-white px-5 py-8 shadow-soft">
                  <div className="flex h-11 w-11 items-center justify-center rounded-[18px] bg-brand/10 text-brand">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <p className="mt-4 text-sm font-medium leading-6 text-ink-soft">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-shell">
        <div className="overflow-hidden rounded-[30px] border border-white/10 bg-night p-8 text-white shadow-glow sm:p-10">
          <SectionHeading
            eyebrow="Start now"
            title="Start fundraising today."
            tone="inverse"
            actions={
              <>
                <Button asChild size="lg">
                  <Link to="/signup">Start free</Link>
                </Button>
                <Button asChild size="lg" variant="dark">
                  <Link to="/pricing">
                    View pricing
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </>
            }
          />
        </div>
      </section>
    </div>
  )
}

export const Component = FeaturesPage
